import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import classnames from 'classnames'

import Button from '../../Button'
import ConditionalWrapper from '../../hoc/ConditionalWrapper'

import useStyles from './styles'

function ItemsLoading({
  loadMore,
  itemsLength = 0,
  isLoadingSelector,
  wasLoadedSelector,
  errorSelector,
  className,
  children,
  additionalDataIsLoading,
  noDataText = 'Nothing to see here!',
  isEmptyNoDataMessage,
  // be careful setting big numbers due to performance issues with a big amount of skeletons
  initialLoadingSkeletonsNumber = 3,
  loadingMoreSkeletonsNumber,
  SkeletonsWrapper = React.Fragment,
  SkeletonComponent,
  skeletonProps,
  ContentWrapper
}) {
  const classes = useStyles()

  const { t } = useTranslation()
  const [isLoadingMoreItems, setIsLoadingMoreItems] = useState(false)
  const [loadMoreScrollPosition, setLoadMoreScrollPosition] = useState(0)

  const itemsIsLoading = useSelector(isLoadingSelector)
  const itemsWasLoaded = useSelector(wasLoadedSelector)
  const itemsError = useSelector(errorSelector)

  const setLoadMoreScrollPositionHandler = useCallback(() => {
    setLoadMoreScrollPosition(document.documentElement.scrollTop || document.body.scrollTop)
  }, [])

  const loadMoreItemsHandler = useCallback(() => {
    loadMore()
    setIsLoadingMoreItems(true)

    setLoadMoreScrollPositionHandler()
  }, [loadMore, setLoadMoreScrollPositionHandler])

  const itemsIsEmpty = itemsLength === 0

  const isInitialLoading = (itemsIsLoading && itemsIsEmpty) || additionalDataIsLoading

  // filtering means that items were loaded, but new request running (some filters were changed)
  const isFilteringRunning = itemsWasLoaded && itemsIsLoading

  // show if first initial loading or we loading more items
  const showBasicLoading = isInitialLoading || isLoadingMoreItems

  // show if initial loading was done and items are not filtering, or we loading more items
  const showMainContent = (!isInitialLoading && !isFilteringRunning && !itemsIsEmpty) || isLoadingMoreItems

  const showFilteringLoading = !isInitialLoading && !isLoadingMoreItems && isFilteringRunning

  // request done, but data is empty or received error
  const showNoDataMessage =
    itemsIsEmpty && (itemsWasLoaded || itemsError) && !itemsIsLoading && !additionalDataIsLoading

  useEffect(() => {
    // track user scrolling after start of loading more items
    const handleLoadMoreScroll = () => {
      if (isLoadingMoreItems) {
        setLoadMoreScrollPositionHandler()
      }
    }

    window.addEventListener('scroll', handleLoadMoreScroll)

    return () => {
      window.removeEventListener('scroll', handleLoadMoreScroll)
    }
  }, [isLoadingMoreItems, setLoadMoreScrollPositionHandler])

  useEffect(() => {
    // reset Load more loading state to false after additional items loads
    if (!itemsIsLoading && isLoadingMoreItems) {
      const currentScrollPosition = document.documentElement.scrollTop || document.body.scrollTop

      setIsLoadingMoreItems(false)

      // sometimes scroll position can jump to the bottom of the page after loading more items
      // in this case we need to restore it to the previous position
      if (loadMoreScrollPosition && currentScrollPosition > loadMoreScrollPosition) {
        window.scrollTo(0, loadMoreScrollPosition)
      }

      setLoadMoreScrollPosition(0)
    }
  }, [isLoadingMoreItems, itemsIsLoading, loadMoreScrollPosition])

  if (showNoDataMessage) {
    return isEmptyNoDataMessage ? null : <p className={classes.noDataMessage}>{noDataText}</p>
  }

  return (
    <>
      <ConditionalWrapper
        condition={!!ContentWrapper}
        wrapper={children => <ContentWrapper>{children}</ContentWrapper>}
      >
        {/* don't change this class name, it's used in PDF generation*/}
        <ul className={classnames(classes.list, className)}>
          {showMainContent && children}
          {showBasicLoading && (
            <SkeletonsWrapper>
              {Array(initialLoadingSkeletonsNumber)
                .fill(Math.random())
                .map((item, index) => (
                  <SkeletonComponent key={index} {...skeletonProps} />
                ))}
            </SkeletonsWrapper>
          )}

          {showFilteringLoading && (
            <SkeletonsWrapper>
              {Array(loadingMoreSkeletonsNumber || (itemsLength <= 15 ? itemsLength : 15))
                .fill(Math.random())
                .map((item, index) => (
                  <SkeletonComponent key={index} {...skeletonProps} />
                ))}
            </SkeletonsWrapper>
          )}
        </ul>
        {loadMore && !isLoadingMoreItems && !isInitialLoading && (
          <Button className={classes.loadMoreBtn} onClick={loadMoreItemsHandler}>
            {t('Load more')}
          </Button>
        )}
      </ConditionalWrapper>
    </>
  )
}

ItemsLoading.propTypes = {
  loadMore: PropTypes.func,
  itemsLength: PropTypes.number,
  className: PropTypes.string,
  children: PropTypes.node,
  noDataText: PropTypes.node,
  isEmptyNoDataMessage: PropTypes.bool,
  isLoadingSelector: PropTypes.func.isRequired,
  wasLoadedSelector: PropTypes.func.isRequired,
  errorSelector: PropTypes.func.isRequired,
  additionalDataIsLoading: PropTypes.bool,
  SkeletonComponent: PropTypes.func,
  initialLoadingSkeletonsNumber: PropTypes.number,
  loadingMoreSkeletonsNumber: PropTypes.number,
  skeletonProps: PropTypes.object,
  ContentWrapper: PropTypes.func
}

export default ItemsLoading
